import { useEffect, useMemo, useRef, useState } from 'react'

import { Box, Icon, PopoverMenu, Text } from '@factorialco/deprecated-design-system'
import { Alert, AlertDescription, AlertTitle } from '@factorialco/f0-react/dist/experimental'
import {
  ProjectManagementDefaultRateCostDocument,
  ProjectManagementDefaultRateCostQuery,
  ProjectManagementDefaultRateCostQueryVariables,
} from 'generated/resources/graphql'

import LabeledInput from 'modules/core/components/LabeledInput'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import RateConfigurationCard from 'modules/project_management/components/ProfitabilityCards/RateConfigurationCard'
import SkeletonLoaderCard from 'modules/project_management/components/ProfitabilityCards/RateConfigurationCard/SkeletonLoaderCard'
import RolesAndLevelsEntitySelector from 'modules/project_management/components/tables/JobsAndRatesDataCollection/RolesAndLevelsSelector'
import { SelectedLevelItem } from 'modules/project_management/components/tables/JobsAndRatesDataCollection/types'
import { DEFAULT_CURRENCY } from 'modules/project_management/lib/constants'
import { getLevelsDisplayText } from 'modules/project_management/lib/formatText'
import { ProjectManagementRateCreateInput } from 'modules/project_management/pages/ProjectDetailsPage/Employees/RatesSettingsSidepanel/types'

interface JobBasedRatesProps {
  rates: ProjectManagementRateCreateInput[]
  onChange: (rates: ProjectManagementRateCreateInput[]) => void
}

const JobBasedRates = ({ rates, onChange }: JobBasedRatesProps) => {
  const [cachedRateCosts, setCachedRateCosts] = useState<Record<number, number>>({})
  const requestedLevelIds = useRef<Set<number>>(new Set())

  const selectedLevels = useMemo(
    () =>
      rates.map((rate) => ({
        id: rate.resourceId.toString(),
        name: rate.name || '',
        type: 'level' as const,
        totalCount: 0,
        salaryRanges: [],
      })),
    [rates]
  )

  const levelIds = useMemo(() => rates.map((rate) => rate.resourceId), [rates])

  const uncachedLevelIds = useMemo(
    () => levelIds.filter((id) => !(id in cachedRateCosts) && !requestedLevelIds.current.has(id)),
    [levelIds, cachedRateCosts]
  )

  const [fetchDefaultRateCosts, { data: defaultRateCostsData }] = useLazyQuery<
    ProjectManagementDefaultRateCostQuery,
    ProjectManagementDefaultRateCostQueryVariables
  >(ProjectManagementDefaultRateCostDocument)

  const uncachedLevelIdsString = uncachedLevelIds.join(',')
  useEffect(() => {
    if (uncachedLevelIds.length > 0) {
      uncachedLevelIds.forEach((id) => requestedLevelIds.current.add(id))
      fetchDefaultRateCosts({
        variables: {
          resourceIds: uncachedLevelIds,
          resourceType: 'JobCatalog::TreeLevel',
        },
      })
    }
  }, [uncachedLevelIdsString, fetchDefaultRateCosts, uncachedLevelIds])

  useEffect(() => {
    if (defaultRateCostsData?.projectManagement?.defaultRateCostsConnection?.nodes) {
      const newCacheEntries: Record<number, number> = {}
      defaultRateCostsData.projectManagement.defaultRateCostsConnection.nodes.forEach(
        (node: { resourceId: number; chargingCostCents: number }) => {
          newCacheEntries[node.resourceId] = node.chargingCostCents
        }
      )
      setCachedRateCosts((prev) => ({ ...prev, ...newCacheEntries }))
    }
  }, [defaultRateCostsData])

  useEffect(() => {
    if (Object.keys(cachedRateCosts).length === 0) return

    const updatedRates = rates.map((rate) => {
      const costCents = cachedRateCosts[rate.resourceId]
      if (costCents !== undefined && rate.chargingCostCents === 0) {
        return {
          ...rate,
          chargingCostCents: costCents,
          billingRateCents: costCents,
        }
      }
      return rate
    })

    const hasChanges = updatedRates.some(
      (rate, idx) => rate.chargingCostCents !== rates[idx]?.chargingCostCents
    )

    if (hasChanges) {
      onChange(updatedRates)
    }
  }, [cachedRateCosts, rates, onChange])

  const handleLevelsChange = (values: { levels?: SelectedLevelItem[] }) => {
    const newLevels = values.levels || []
    const newLevelIds = newLevels.map((l) => l.id)

    const filteredRates = rates.filter((rate) => newLevelIds.includes(rate.resourceId.toString()))

    const newRates = [...filteredRates]
    newLevels.forEach((level) => {
      if (!newRates.some((rate) => rate.resourceId.toString() === level.id)) {
        const levelIdNum = parseInt(level.id, 10)
        const costCents = cachedRateCosts[levelIdNum] || 0
        newRates.push({
          resourceId: levelIdNum,
          resourceType: 'JobCatalog::TreeLevel',
          billingRateCents: costCents,
          chargingCostCents: costCents,
          profitabilityPercentageCents: 0,
          currency: DEFAULT_CURRENCY,
          name: level.name,
        })
      }
    })

    onChange(newRates)
  }

  return (
    <Box gap="s16">
      <PopoverMenu type="dialog">
        <PopoverMenu.Trigger>
          <LabeledInput
            isLegend
            label={i18n.t('project_management.jobs_and_rates.sidepanels.form.select_jobs.label')}
          >
            <Box
              gap="s8"
              flexDirection="row"
              justifyContent="spaceBetween"
              alignItems="center"
              border={{
                all: {
                  width: 's2',
                  style: 'solid',
                  color: 'grey400',
                },
              }}
              borderRadius={{
                all: 'abs008',
              }}
              padding="s8"
              paddingX="s16"
              cursor="pointer"
            >
              <Text.Normal ellipsis>{getLevelsDisplayText(selectedLevels.length)}</Text.Normal>
              <Icon.Small icon="caret-down" color="grey600" />
            </Box>
          </LabeledInput>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="center" width="s400" hideWhenDetached>
          <Box alignItems="center">
            <RolesAndLevelsEntitySelector
              selectedLevels={selectedLevels}
              setValues={handleLevelsChange}
              existingResourceJobLevelIds={[]}
            />
          </Box>
        </PopoverMenu.Content>
      </PopoverMenu>
      {rates.length > 0 && (
        <Box paddingY="s24">
          <Alert variant="warning">
            <AlertTitle>
              {i18n.t('project_management.forms.billing_rate.alerts.changes_effective.title')}
            </AlertTitle>
            <AlertDescription>
              {i18n.t('project_management.forms.billing_rate.alerts.changes_effective.description')}
            </AlertDescription>
          </Alert>
        </Box>
      )}
      {rates.map((rate) => {
        const level = selectedLevels.find((l) => l.id === rate.resourceId.toString())
        const hasCostData = cachedRateCosts[rate.resourceId] !== undefined

        if (!hasCostData) {
          return <SkeletonLoaderCard key={rate.resourceId} />
        }

        return (
          <RateConfigurationCard
            key={rate.resourceId}
            currency={rate.currency}
            maxCostRate={rate.chargingCostCents / 100}
            onChangeValue={(rateValues: { billRate: string; minProfitMargin: string }) => {
              const billRateCents = Math.round(parseFloat(rateValues.billRate) * 100)
              const profitabilityPercentageCents = Math.round(
                parseFloat(rateValues.minProfitMargin) * 100
              )
              onChange(
                rates.map((existingRate) =>
                  existingRate.resourceId === rate.resourceId
                    ? {
                        ...existingRate,
                        billingRateCents: billRateCents,
                        profitabilityPercentageCents: profitabilityPercentageCents,
                      }
                    : existingRate
                )
              )
            }}
            onDelete={() => {
              onChange(rates.filter((r) => r.resourceId !== rate.resourceId))
            }}
            peopleCount={level?.totalCount || 0}
            title={rate.name || ''}
            disabledAllFields={false}
            profitMargin={rate.profitabilityPercentageCents / 100}
            initialBillRate={(rate.billingRateCents / 100).toFixed(2)}
          />
        )
      })}
    </Box>
  )
}

export default JobBasedRates
